import { useMemo } from 'react';

import { useUserUpdateNotificationSettings } from 'api/requests';
import {
  NotificationSettingsData,
  NotificationSettingsType,
} from 'api/types/data';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { useUser } from 'libs/providers';

import {
  NotificationsFormSubmit,
  NotificationsFormType,
} from '../forms/notificationsForm';

export const useNotificationSettings = () => {
  const { user } = useUser();

  const notify = useNotify();
  const { t } = useTranslation();

  const { loading: submitLoading, updateNotificationSettings } =
    useUserUpdateNotificationSettings();

  const userNotifications = useMemo(
    () =>
      user?.settings.notifications?.map((n) => ({
        id: n.type,
        isEmailEnabled: n.isEmailEnabled,
        isBellEnabled: n.isBellEnabled,
        name: t(`settings.notifications.types.${n.type}`),
      })) ?? [],
    [t, user?.settings.notifications]
  );

  const initialValues: NotificationsFormType = useMemo(() => {
    const onboardingStatus = userNotifications.find(
      (notification) =>
        notification.id === NotificationSettingsType.ChangeOnboardingStatus
    );

    return {
      onboardingStatus: {
        isBellEnabled: onboardingStatus?.isBellEnabled ?? false,
        isEmailEnabled: onboardingStatus?.isEmailEnabled ?? false,
      },
    };
  }, [userNotifications]);

  const onSubmit: NotificationsFormSubmit = async (values) => {
    const data: NotificationSettingsData[] = [
      {
        type: NotificationSettingsType.ChangeOnboardingStatus,
        isEmailEnabled: values.onboardingStatus.isEmailEnabled,
        isBellEnabled: values.onboardingStatus.isBellEnabled,
      },
    ];

    if (await updateNotificationSettings(data)) {
      notify.info(t('common.successSave'));
    }
  };

  return {
    submitLoading,
    initialValues,
    onSubmit,
  };
};
